/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import { QuickOrderTemplate } from '@msdyn365-commerce-modules/retail-actions';
import { Alert, Button, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { AddToCartProductListComponent } from './add-to-list';
import { IQuickOrderGridActionsViewProps, IQuickOrderGridState } from './utilities';

/**
 * Interface for quick order grid action data.
 */
export interface IQuickOrderGridActionsData {
    quickOrderTemplate: QuickOrderTemplate | undefined;
}

/**
 * Interface for quick order grid action props.
 */
export interface IQuickOrderGridActionsProps extends IComponentProps<IQuickOrderGridActionsData> {
    context: ICoreContext;
    telemetryContent?: ITelemetryContent;
    moduleClassName: string;
    typeName: string;
    handlers: {
        removeProductList(productListId: string): void;
        onErrorMessage(message: string): void;
    };
    quickOrderGridState: IQuickOrderGridState;
    resources: {
        quickOrderGridDeleteButtonText: string;
        quickOrderGridBuySelectedButtonText?: string;
        quickOrderGridBuySelectedButtonAriaLabel?: string;
        quickOrderGridBuySelectedAddingToCartErrorNotificationTitle?: string;
        quickOrderGridBuySelectedAddingToCartErrorNotificationCloseAriaLabel?: string;
        quickOrderGridGoToCartText?: string;
        quickOrderGridDialogCloseText?: string;
        quickOrderGridSingleItemText?: string;
        quickOrderGridMultiLineItemFormatText?: string;
        quickOrderGridMultiLinesFormatText?: string;
        quickOrderGridHeaderMessageText?: string;
        addedQuantityText?: string;
        originalPriceText?: string;
        currentPriceText?: string;
        freePriceText?: string;
    };
}

/**
 * Remove ProductList Action.
 * @param props -Quick order grid action props.
 */
const removeQuickOrderList = (props: IQuickOrderGridActionsProps): void => {
    const {
        data: { quickOrderTemplate },
        handlers
    } = props;
    const { removeProductList } = handlers;
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- Need to check data.
    if (quickOrderTemplate && quickOrderTemplate.productList.Id) {
        removeProductList(quickOrderTemplate.productList.Id);
    }
};

/**
 * Get Addtobag Button.
 * @param props -Order template action props.
 * @returns QuickOrderGrid actions view props.
 */
const getQuickOrderGridAddToCartDialog = (props: IQuickOrderGridActionsProps): React.ReactNode => {
    const {
        data: { quickOrderTemplate },
        context,
        moduleClassName,
        resources,
        typeName
    } = props;
    const buttonId = 'quick-order-grid';
    return (
        <AddToCartProductListComponent
            className={`${moduleClassName}-action-bar__buy-selected msc-btn`}
            id={buttonId}
            typeName={typeName}
            data={{ quickOrderTemplate }}
            context={context}
            resources={resources}
            onError={(result): void => {
                if (result.cartActionResult?.errorDetails?.LocalizedMessage) {
                    props.handlers.onErrorMessage(result.cartActionResult.errorDetails.LocalizedMessage);
                }
            }}
        />
    );
};

/**
 * Remove Quick order grid action.
 * @param props -Quick order grid action props.
 * @returns Remove order template.
 */
const removeActionHandler = (props: IQuickOrderGridActionsProps) => () => {
    removeQuickOrderList(props);
};

/**
 * Quick order grid action view.
 * @param props -Quick Order Grid template action props.
 * @returns QuickOrderGrid actions view props.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention -- Name needs to be this.
export const QuickOrderGridActionsView = (props: IQuickOrderGridActionsProps): IQuickOrderGridActionsViewProps => {
    const { moduleClassName, quickOrderGridState, resources } = props;
    const { quickOrderGridDeleteButtonText } = resources;

    const userMessageNode = quickOrderGridState.topStateMessage ? (
        <Alert color={quickOrderGridState.topStateMessage.statusCssString} isOpen={quickOrderGridState.topStateMessage.isOpen}>
            <span className={quickOrderGridState.topStateMessage.messageGlyph} />
            <span>{quickOrderGridState.topStateMessage.messageText}</span>
        </Alert>
    ) : (
        undefined
    );

    return {
        addProductListToBag: getQuickOrderGridAddToCartDialog(props),
        removeProductList: (
            <Button className={`${moduleClassName}-action-bar__delete-list-button`} onClick={removeActionHandler(props)}>
                {quickOrderGridDeleteButtonText}
            </Button>
        ),
        userMessage: userMessageNode
    };
};
